import React from 'react';

/**
 * Splices an interstitial node (e.g. the top-employers strip) into a flat results grid at fixed
 * positions — after the 3rd card (single-column / mobile) and after the 6th card (2 completed
 * rows of the 3-column desktop grid). Both copies are always rendered; CSS container queries
 * (matching the 48rem/64rem grid breakpoints in hit-card.css) show exactly one at a time, so
 * placement needs no client-side measurement and stays correct through SSR/hydration.
 *
 * `interstitial` is null when nothing should render (e.g. no employers, gated by location/map/page).
 *
 * `secondInterstitial` (e.g. the top-roles strip) lands 2 grid rows further down than
 * `interstitial` in each layout: after the 5th card on mobile (1 column, so +2 rows = +2 cards)
 * and after the 12th card on desktop (3 columns, so +2 rows = +6 cards).
 */
export function spliceResultsInterstitial<T>(
  items: T[],
  renderItem: (item: T, index: number) => React.ReactNode,
  interstitial: React.ReactNode | null,
  keyPrefix = 'srp-interstitial',
  secondInterstitial: React.ReactNode | null = null
): React.ReactNode[] {
  const nodes: React.ReactNode[] = [];
  items.forEach((item, index) => {
    nodes.push(renderItem(item, index));
    if (interstitial) {
      if (index === 2) {
        nodes.push(
          <li
            key={`${keyPrefix}-mobile`}
            className="srp-results-grid-interstitial block @[768px]:hidden"
          >
            {interstitial}
          </li>
        );
      }
      if (index === 5) {
        nodes.push(
          <li
            key={`${keyPrefix}-desktop`}
            className="srp-results-grid-interstitial hidden @[768px]:block"
          >
            {interstitial}
          </li>
        );
      }
    }
    if (secondInterstitial) {
      if (index === 4) {
        nodes.push(
          <li
            key={`${keyPrefix}-2-mobile`}
            className="srp-results-grid-interstitial block @[768px]:hidden"
          >
            {secondInterstitial}
          </li>
        );
      }
      if (index === 11) {
        nodes.push(
          <li
            key={`${keyPrefix}-2-desktop`}
            className="srp-results-grid-interstitial hidden @[768px]:block"
          >
            {secondInterstitial}
          </li>
        );
      }
    }
  });
  return nodes;
}
